import { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Dismissed recently (7 days)
    const dismissed = localStorage.getItem('pwa-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS: show instructions after 30s
    if (isIOSDevice) {
      const t = setTimeout(() => setShowBanner(true), 30000);
      return () => { clearTimeout(t); window.removeEventListener('beforeinstallprompt', handler); };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  async function install() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShowBanner(false);
    setDeferredPrompt(null);
  }

  function dismiss() {
    setShowBanner(false);
    localStorage.setItem('pwa-dismissed', Date.now().toString());
  }

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[999] animate-[slideUp_0.3s_ease]">
      <style>{`@keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
      <div className="bg-dark-800 border border-dark-600 rounded-2xl p-4 shadow-2xl flex items-center gap-3 max-w-md mx-auto">
        <div className="w-10 h-10 rounded-xl bg-accent-primary/15 flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-5 h-5 text-accent-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">Install haythix</p>
          {isIOS ? (
            <p className="text-xs text-gray-500 mt-0.5">
              Tap <span className="text-white">Share</span> then <span className="text-white">"Add to Home Screen"</span>
            </p>
          ) : (
            <p className="text-xs text-gray-500 mt-0.5">Faster access, works offline</p>
          )}
        </div>
        {!isIOS && (
          <button onClick={install}
            className="px-4 py-2 rounded-xl text-xs font-semibold bg-gradient-to-r from-amber-400 to-orange-400 text-dark-900 hover:from-amber-300 hover:to-orange-300 transition-all flex-shrink-0">
            Install
          </button>
        )}
        <button onClick={dismiss} className="p-1.5 text-gray-600 hover:text-gray-400 transition-colors flex-shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export function OfflineBanner() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  if (isOnline) return null;

  return (
    <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-2 text-center">
      <p className="text-xs text-amber-400 font-medium">
        You're offline — showing cached data. Some features may be unavailable.
      </p>
    </div>
  );
}
